import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { TokenService } from "@/gen/omnios/v1/token_connect.js";
import { transport } from "@/lib/grpc";
import { createClient } from "@connectrpc/connect";
import { createFileRoute } from "@tanstack/react-router";
import { Check, Terminal, X } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/device")({
	component: DeviceAuthPage,
});

const client = createClient(TokenService, transport);

function DeviceAuthPage() {
	const [code, setCode] = useState("");
	const [status, setStatus] = useState<
		"idle" | "approved" | "denied" | "error"
	>("idle");
	const [submitting, setSubmitting] = useState(false);
	const { t } = useTranslation();

	const handleApprove = async () => {
		setSubmitting(true);
		try {
			await client.approveDeviceAuthorization({
				userCode: code.trim().toUpperCase(),
			});
			setStatus("approved");
		} catch {
			setStatus("error");
		} finally {
			setSubmitting(false);
		}
	};

	const handleDeny = async () => {
		setSubmitting(true);
		try {
			await client.denyDeviceAuthorization({
				userCode: code.trim().toUpperCase(),
			});
			setStatus("denied");
		} catch {
			setStatus("error");
		} finally {
			setSubmitting(false);
		}
	};

	if (status === "approved") {
		return (
			<div className="flex min-h-screen items-center justify-center">
				<div className="w-full max-w-sm space-y-4 rounded-lg border p-6 text-center">
					<Check className="mx-auto h-12 w-12 text-green-500" />
					<h1 className="text-lg font-semibold">{t("device_authorized")}</h1>
					<p className="text-sm text-muted-foreground">
						{t("device_authorized_hint")}
					</p>
				</div>
			</div>
		);
	}

	if (status === "denied") {
		return (
			<div className="flex min-h-screen items-center justify-center">
				<div className="w-full max-w-sm space-y-4 rounded-lg border p-6 text-center">
					<X className="mx-auto h-12 w-12 text-destructive" />
					<h1 className="text-lg font-semibold">{t("device_denied")}</h1>
					<p className="text-sm text-muted-foreground">
						{t("device_denied_hint")}
					</p>
				</div>
			</div>
		);
	}

	return (
		<div className="flex min-h-screen items-center justify-center">
			<div className="w-full max-w-sm space-y-6 rounded-lg border p-6">
				<div className="text-center">
					<Terminal className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
					<h1 className="text-lg font-semibold">{t("device_auth_title")}</h1>
					<p className="text-sm text-muted-foreground">
						{t("device_auth_hint")}
					</p>
				</div>

				<Input
					placeholder="ABCD-EFGH"
					value={code}
					onChange={(e) => setCode(e.target.value.toUpperCase())}
					className="text-center text-lg font-mono tracking-widest"
					maxLength={9}
				/>

				{status === "error" && (
					<p className="text-sm text-destructive text-center">
						{t("device_code_error")}
					</p>
				)}

				<div className="flex gap-2">
					<Button
						variant="outline"
						className="flex-1"
						onClick={handleDeny}
						disabled={code.trim().length < 9 || submitting}
					>
						{t("deny")}
					</Button>
					<Button
						className="flex-1"
						onClick={handleApprove}
						disabled={code.trim().length < 9 || submitting}
					>
						{t("authorize")}
					</Button>
				</div>
			</div>
		</div>
	);
}
